import { useEffect, useRef, useState } from 'react'

interface StoryImageProps {
  /** Адрес кадра, который должен оказаться на экране. */
  src: string
  className: string
  /** Кадр загрузился и показан — по нему заводится таймер показа слайда. */
  onShow: (src: string) => void
  /** Кадр не скачался: сеть или битая ссылка. */
  onError: (src: string) => void
}

/**
 * Картинка слайда, которая не моргает при переключении.
 *
 * Раньше у `<img>` менялся `key`, старый элемент выбрасывался, и пока грузился
 * новый кадр, в сцене не было ничего — проступала размытая подложка. Здесь
 * новый адрес сначала скачивается и декодируется в стороне, и только готовый
 * кадр подменяет предыдущий: промежуточного пустого состояния не возникает.
 */
export const StoryImage = ({ src, className, onShow, onError }: StoryImageProps) => {
  const [shown, setShown] = useState(src)

  // Колбэки держим в рефах: иначе смена их ссылки перезапускала бы загрузку.
  const onShowRef = useRef(onShow)
  onShowRef.current = onShow
  const onErrorRef = useRef(onError)
  onErrorRef.current = onError

  useEffect(() => {
    let cancelled = false

    const show = () => {
      if (cancelled) return
      setShown(src)
      onShowRef.current(src)
    }

    const image = new Image()
    image.src = src

    // Декодируем заранее: без этого браузер разбирает картинку в кадре отрисовки
    // и на слабых телефонах переключение дёргается. decode() есть не везде и
    // может отклониться — тогда просто показываем по onload.
    const loaded = () => {
      if (cancelled) return
      if (typeof image.decode !== 'function') {
        show()
        return
      }
      image.decode().then(show, show)
    }

    if (image.complete && image.naturalWidth > 0) {
      loaded()
      return () => {
        cancelled = true
      }
    }

    image.onload = loaded
    image.onerror = () => {
      if (cancelled) return
      onErrorRef.current(src)
      // Показываем всё равно: иначе на экране навсегда остался бы чужой кадр.
      setShown(src)
    }

    return () => {
      cancelled = true
    }
  }, [src])

  return <img className={className} src={shown} alt="" />
}
